import { useContext } from 'react';
import styled from '@emotion/styled';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { ExtractedAnswersList } from '@trinity/chat-components/components/ExtractedAnswers/index.js';
import { ExtractedAnswersAPIContext } from '@trinity/chat-components/contexts/ExtractedAnswersAPIContext.js';
import { useExtractedAnswers } from '@trinity/chat-components/hooks/useExtractedAnswers';
import { TranscriptIdContext } from '@/containers/Transcript/context';
import { Skeleton } from '@/components/Transcript';

const Container = styled.div`
  padding: 16px;
  overflow-y: auto;
  max-height: 100%;
`;

const ErrorMessage = styled.div`
  color: #991b1b;
  padding: 16px;
  text-align: center;
`;

function ExtractedAnswersContent(): React.ReactElement {
  const transcriptId = useContext(TranscriptIdContext);

  const { data, isLoading, error } = useExtractedAnswers(transcriptId, {
    enabled: !!transcriptId,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  if (isLoading) return <Skeleton.Summary rows={3} />;
  if (error) return <ErrorMessage>Failed to load extracted answers.</ErrorMessage>;

  return (
    <Container>
      <ExtractedAnswersList items={data?.items ?? []} />
    </Container>
  );
}

export function ExtractedAnswers(): React.ReactElement {
  const apiClient = useChatApiClient();

  const contextValue = {
    getExtractedAnswers: apiClient.transcripts.getExtractedAnswers.bind(apiClient.transcripts),
  };

  return (
    <ExtractedAnswersAPIContext.Provider value={contextValue}>
      <ExtractedAnswersContent />
    </ExtractedAnswersAPIContext.Provider>
  );
}

ExtractedAnswers.displayName = 'Tab.ExtractedAnswers';
